import { useEffect, useRef } from 'react';

/**
 * Shared polling primitive — Sebastian ruling 2026-08-12 evening
 * ("kein Live-Update-Pfad" defect on the plan; B7). Extracted from the
 * three ad-hoc `setInterval` implementations that already exist in the
 * codebase:
 *
 *   - `hooks/useUnreadNotificationCount.ts` — 30s notification badge
 *   - `features/story/use-story-detail.ts`  — 4s while assets in transit
 *   - `features/credits/useCheckoutPolling.ts` — 2s post-Stripe webhook
 *
 * Those three keep their inline implementations for now (Sebastian
 * ruling: "do not refactor the three existing polling hooks — they
 * work"). New consumers compose from this primitive to avoid a fourth
 * copy of the same boilerplate.
 *
 * FIRST CONSUMER (this wave): `features/book/use-preview-envelope.ts`
 * → poll the book envelope every 5 seconds while status is a backend-
 * driven non-terminal state (light_gate_pending, anchor_pending,
 * story_generating, preview_rendering). Sebastian: on order 65c485ed
 * tonight, story_generating alone lasted ~5 minutes; without polling
 * the customer sat on a wait screen and hand-reloaded through it.
 *
 * SEMANTICS
 * ─────────
 * `shouldPoll` gate reads AT RENDER TIME. When it flips true, the
 * effect arms an interval. When it flips false, the effect tears it
 * down. Same for the guard listeners (visibilitychange, online).
 * No initial `onTick` call — the caller is responsible for the
 * first fetch (typically via a separate mount effect owned by the
 * caller); this primitive only handles subsequent polls.
 *
 * REFS INSTEAD OF DEPS
 * ────────────────────
 * `onTick` is captured in a ref so a caller who re-creates the
 * callback per render (arrow function inline) does not tear the
 * interval down every render. Same pattern as
 * `use-story-detail.ts:104` for the same reason.
 *
 * GUARDS
 * ──────
 * `guardVisibility` (default true): if `document.visibilityState ===
 * 'hidden'` at tick time, skip the tick. On visibilitychange to
 * 'visible', fire one immediate catch-up tick then resume the
 * interval. Prevents a hidden background tab from firing polls the
 * customer will never look at, and gives an instant refresh when the
 * customer returns.
 *
 * `guardOnline` (default true): if `navigator.onLine === false` at
 * tick time, skip. Ties to the browser's online event for symmetry
 * with `useUnreadNotificationCount.ts:11-16`.
 *
 * BOUNDS
 * ──────
 * `intervalMs` must be > 0. The primitive does not clamp — the caller
 * chooses the cadence. Load discipline (don't poll every 100ms) is
 * the caller's problem, not the primitive's.
 *
 * NON-CONSUMERS (deliberately)
 * ────────────────────────────
 * SSE-based hooks (`useStoryJobStream.ts`) do NOT use this — SSE is a
 * push protocol, not a poll.
 * Fetch-once + retry hooks (`useSubscriptionState`, `useEntitlement`)
 * do NOT use this — they intentionally do NOT refresh over time.
 */
export interface UsePollingOptions {
  /**
   * Gate. When true → interval armed. When false → interval torn down.
   * Derived from resource state at render time; the primitive re-
   * evaluates on every render via the effect's dep array.
   */
  readonly shouldPoll: boolean;
  /**
   * Called once per interval tick when all guards allow. Return value
   * ignored (fire-and-forget) — errors are the caller's responsibility
   * to catch inside `onTick`.
   */
  readonly onTick: () => void | Promise<void>;
  /** Milliseconds between ticks. Must be > 0. */
  readonly intervalMs: number;
  /**
   * Pause when `document.visibilityState === 'hidden'`. Default true.
   * On visibilitychange to 'visible', fire one immediate catch-up
   * tick then resume the interval cadence.
   */
  readonly guardVisibility?: boolean;
  /**
   * Skip ticks when `navigator.onLine === false`. Default true.
   * Automatically resumes when the browser's `online` event fires.
   */
  readonly guardOnline?: boolean;
}

export function usePolling(options: UsePollingOptions): void {
  const { shouldPoll, onTick, intervalMs, guardVisibility = true, guardOnline = true } = options;

  // Capture the latest onTick in a ref so callers who inline
  // `onTick: () => refetch()` don't tear the interval down on every
  // render. See house pattern in use-story-detail.ts.
  const onTickRef = useRef(onTick);
  useEffect(() => {
    onTickRef.current = onTick;
  }, [onTick]);

  useEffect(() => {
    if (!shouldPoll) return;
    if (!Number.isFinite(intervalMs) || intervalMs <= 0) return;

    const canRunTick = (): boolean => {
      if (guardVisibility && typeof document !== 'undefined') {
        if (document.visibilityState === 'hidden') return false;
      }
      if (guardOnline && typeof navigator !== 'undefined') {
        if (navigator.onLine === false) return false;
      }
      return true;
    };

    const tick = (): void => {
      if (!canRunTick()) return;
      const result = onTickRef.current();
      if (result && typeof result === 'object' && 'then' in result) {
        // Swallow — caller owns error handling. Prevents an unhandled
        // rejection from a caller who forgets to catch inside onTick.
        (result as Promise<void>).catch(() => undefined);
      }
    };

    const intervalId = window.setInterval(tick, intervalMs);

    // Visibility handler: immediate catch-up tick when the tab becomes
    // visible AND we're still allowed to run. Sebastian ruling:
    // customer returning to the tab after 30s should see fresh state
    // in the next render, not after another intervalMs wait.
    const visibilityHandler = (): void => {
      if (document.visibilityState === 'visible' && canRunTick()) {
        tick();
      }
    };
    // Online handler: same principle for network restore.
    const onlineHandler = (): void => {
      if (canRunTick()) tick();
    };

    if (guardVisibility && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', visibilityHandler);
    }
    if (guardOnline && typeof window !== 'undefined') {
      window.addEventListener('online', onlineHandler);
    }

    return () => {
      window.clearInterval(intervalId);
      if (guardVisibility && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
      if (guardOnline && typeof window !== 'undefined') {
        window.removeEventListener('online', onlineHandler);
      }
    };
  }, [shouldPoll, intervalMs, guardVisibility, guardOnline]);
}
